import React from 'react';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Copy, Database, Network } from 'lucide-react';
import { platformRoutes, platformCtaHref, type PlatformRoute } from './platformRoutes';
import { PlatformPricing } from './PlatformPricing';
import { getUserData } from '../lib/userData';

export const PlatformRoutePage: React.FC<{ route: PlatformRoute }> = ({ route }) => {
  const Icon = route.icon;
  // testMode reads the query string, which only exists on the client. Defer it to
  // after mount so the server-rendered and first client render match (clean
  // hydration); the fixture appears once mounted.
  const [testMode, setTestMode] = React.useState(false);
  React.useEffect(() => {
    setTestMode(new URLSearchParams(window.location.search).get('test') === 'true');
  }, []);
  // Signed-in visitors must skip /signup entirely: it would see the session,
  // redirect to ?next=, and land them right back here with nothing done.
  // Starts false so the prerendered (signed-out) markup hydrates cleanly.
  const [authenticated, setAuthenticated] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    getUserData()
      .then((data) => { if (!cancelled) setAuthenticated(data.authenticated); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const surface = moduleSurfaceFor(route.path);

  return (
    <main className="bg-black bg-[radial-gradient(circle_at_50%_-8%,rgba(255,255,255,0.10),transparent_55%),radial-gradient(circle_at_88%_115%,rgba(120,140,255,0.06),transparent_45%),linear-gradient(rgba(255,255,255,0.022)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.022)_1px,transparent_1px)] bg-[size:100%_100%,100%_100%,48px_48px,48px_48px] px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1800px]">
        <div className="mb-5">
          <a href="/" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/48 hover:text-white">
            <ArrowLeft size={16} />
            Back to overview
          </a>

          <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
                <Icon size={14} />
                {route.eyebrow}
              </div>
              <h1 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-4xl">
                {route.title}
              </h1>
            </div>
            <div>
              <p className="text-sm leading-7 text-white/58">{route.description}</p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                {route.primaryAction && (
                  <a
                    href={platformCtaHref(route, authenticated)}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-400 px-4 text-sm font-bold text-black transition-colors hover:bg-sky-300"
                  >
                    {route.primaryAction} <ArrowRight size={17} />
                  </a>
                )}
                <a
                  href="/docs"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black"
                >
                  Read docs
                </a>
              </div>
            </div>
          </div>
        </div>

        {testMode && (
          <div data-testid="route-test-mode" className="mb-4 rounded-xl border border-white/14 bg-white/[0.06] p-4">
            <div className="grid gap-3 text-xs sm:grid-cols-4">
              <TestStat label="route" value={route.path} />
              <TestStat label="source" value={surface.source} />
              <TestStat label="cards" value={String(route.cards.length)} />
              <TestStat label="command" value={route.command || 'none'} />
            </div>
          </div>
        )}

        {route.command && <CommandBlock command={route.command} />}

        <div className="grid gap-3 lg:grid-cols-[0.78fr_1.22fr]">
          <section className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/34">What you get</div>
                <h2 className="mt-1 font-display text-xl font-bold">{surface.source}</h2>
              </div>
              <Network size={20} className="text-white/42" />
            </div>
            <div className="grid gap-2">
              {surface.functions.map((item) => (
                <div key={item} className="flex items-start gap-2 rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white/58">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-white/48" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="grid gap-3 md:grid-cols-3">
          {route.cards.map((card) => (
            <article key={card.title} data-testid="route-card" className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <h2 className="font-display text-lg font-bold">{card.title}</h2>
              <p className="mt-2 text-sm leading-6 text-white/54">{card.body}</p>
            </article>
          ))}
          </section>
        </div>

        {route.path === '/pricing' && <PlatformPricing />}

        <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.035] p-4">
          <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white/34">Explore more</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {platformRoutes
              .filter((item) => item.path !== route.path)
              .map((item) => {
                const ItemIcon = item.icon;
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-black px-3 py-3 text-sm text-white/58 transition-colors hover:border-white/24 hover:text-white"
                  >
                    <ItemIcon size={16} />
                    {item.label}
                  </a>
                );
              })}
          </div>
        </div>
      </div>
    </main>
  );
};

function CommandBlock({ command }: { command: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = React.useCallback(() => {
    navigator.clipboard
      ?.writeText(command)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [command]);
  return (
    <div className="mb-3 flex w-fit max-w-full items-center gap-2 rounded-xl border border-white/10 bg-[#050505] p-3 font-mono text-sm">
      <span aria-hidden className="select-none text-white/34">$</span>
      <code className="min-w-0 truncate text-white/72">{command}</code>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy command'}
        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs font-semibold text-white/54 transition-colors hover:border-white/24 hover:text-white"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
  );
}

function TestStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-white/10 bg-black px-3 py-2">
      <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/34">
        <Database size={11} />
        {label}
      </div>
      <div data-testid={`route-stat-${label}`} className="truncate font-mono text-white/72">
        {value}
      </div>
    </div>
  );
}

function moduleSurfaceFor(path: string) {
  const map: Record<string, { source: string; functions: string[] }> = {
    '/chat': {
      source: 'Chat and research',
      functions: ['AI chat', 'Live web search', 'Deep research', 'Paper search'],
    },
    '/agents': {
      source: 'Cloud coding agents',
      functions: ['Repo tasks', 'Pull requests', 'VisualBench', 'CI repair'],
    },
    '/pricing': {
      source: 'Simple credits',
      functions: ['Pay as you go', 'Search pricing', 'Model hosting', 'Spend caps'],
    },
    '/projects': {
      source: 'Everything in one project',
      functions: ['Apps and repos', 'Agents and tasks', 'Domains and keys', 'Usage and billing'],
    },
    '/sites': {
      source: 'Sites you control',
      functions: ['In-browser editor', 'Instant publish', 'Public pages', 'Agent-editable'],
    },
    '/deploys': {
      source: 'AI app hosting',
      functions: ['Container deploys', 'Preview URLs', 'GPU inference', 'One-click rollback'],
    },
    '/gateway': {
      source: 'Model gateway',
      functions: ['OpenAI-compatible API', 'Automatic routing', 'Web & paper search', 'Usage you can read'],
    },
    '/workers': {
      source: 'CPU and GPU workers',
      functions: ['Builds and deploys', 'Agent sandboxes', 'Model inference', 'Live heartbeats'],
    },
    '/queues': {
      source: 'Agent task queues',
      functions: ['JSON messages', 'Retries + dead-letter', 'Remote consumers', 'Agent dispatch'],
    },
    '/domains': {
      source: 'Domains made easy',
      functions: ['Free app.nz subdomains', 'Reserved names', 'Custom domains', 'Automatic HTTPS'],
    },
    '/secrets': {
      source: 'Secrets, scoped',
      functions: ['Bring your own keys', 'Per-app env vars', 'Per-deploy secrets', 'Rotation audit'],
    },
    '/logs': {
      source: 'One clear trail',
      functions: ['Agent logs', 'Deploy logs', 'Gateway events', 'Linked artifacts'],
    },
    '/billing': {
      source: 'One simple balance',
      functions: ['Shared credits', 'Gateway usage', 'Spend caps', 'Worker minutes'],
    },
    '/docs': {
      source: 'Docs by task',
      functions: ['Getting started', 'CLI reference', 'Migration guides', 'Team setup'],
    },
    '/cli': {
      source: 'CLI for the agent cloud',
      functions: ['Agent commands', 'Deploy commands', 'Model gateway', 'Key rotation'],
    },
    '/consulting': {
      source: 'Hands-on help',
      functions: ['Migrations', 'Private machines', 'Custom agents', 'Ongoing support'],
    },
    '/schema': {
      source: 'Built to last',
      functions: ['Tested upgrades', 'Predictable behavior', 'Scales with you', 'No surprises'],
    },
    '/security': {
      source: 'Separate by design',
      functions: ['Protected secrets', 'Scoped access', 'Sandboxed code', 'Clear boundaries'],
    },
    '/status': {
      source: 'Live status',
      functions: ['Health checks', 'Service status', 'Region status', 'Worker status'],
    },
    '/workflows': {
      source: 'Reusable workflows',
      functions: ['VisualBench', 'CI repair', 'Deploy checks', 'Team recipes'],
    },
  };
  return map[path] || { source: 'Everything you need', functions: ['Everyday use', 'One API', 'CLI access', 'Always tested'] };
}
